/**
 * User menu dropdown for header
 * Shows user info, profile link, and sign out
 */

import { useState, useEffect } from 'react';
import { User, LogOut, Settings as SettingsIcon, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getBetaUser, type BetaUser } from '@/lib/betaAuth';
import { loadUserProfile, type UserProfile } from '@/lib/userProfile';
import { getAuthInstance } from '@/lib/firebase';
import type { User as FirebaseUser } from 'firebase/auth';
import { Badge } from '@/components/ui/badge';

interface UserMenuProps {
  onProfileClick?: () => void;
  onSignOut?: () => void;
}

export function UserMenu({ onProfileClick, onSignOut }: UserMenuProps) {
  const [betaUser, setBetaUser] = useState<BetaUser | null>(() => getBetaUser());
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Listen to Firebase auth state
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    (async () => {
      try {
        const auth = await getAuthInstance();
        const { onAuthStateChanged } = await import('firebase/auth');
        unsubscribe = onAuthStateChanged(auth, async (user) => {
          setFirebaseUser(user);
          if (user) {
            // Load user profile
            const profile = await loadUserProfile(user.uid);
            setUserProfile(profile);
          } else {
            setUserProfile(null);
          }
          setLoading(false);
        });
      } catch (err) {
        console.error('Auth init failed in UserMenu:', err);
        setLoading(false);
      }
    })();
    return () => unsubscribe?.();
  }, []);

  // Listen to beta user changes
  useEffect(() => {
    const checkBetaUser = () => {
      setBetaUser(getBetaUser());
    };
    // Check periodically (in case beta user changes)
    const interval = setInterval(checkBetaUser, 1000);
    return () => clearInterval(interval);
  }, []);

  const displayName = userProfile?.displayName || betaUser?.name || firebaseUser?.displayName || 'User';
  const email = userProfile?.email || betaUser?.email || firebaseUser?.email || '';
  const isProfileComplete = userProfile?.profileComplete ?? false;
  const profileBadge = (displayName || 'U').trim().charAt(0).toUpperCase() || 'U';

  // Don't render if no user
  if (!betaUser && !firebaseUser) return null;
  if (loading) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full relative">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 text-primary text-xs font-semibold border border-primary/30">
            {profileBadge}
          </span>
          {!isProfileComplete && (
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-amber-500" aria-hidden />
          )}
          <span className="sr-only">Open profile menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-3 py-2">
          <p className="text-sm font-semibold truncate">{displayName}</p>
          <p className="text-xs text-muted-foreground truncate">{email}</p>
          {betaUser && (
            <p className="text-xs text-muted-foreground mt-1">
              Role: <span className="font-semibold text-foreground">{betaUser.role}</span>
            </p>
          )}
          {!isProfileComplete && (
            <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">
              ⚠️ Profile incomplete
            </p>
          )}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onProfileClick} className="cursor-pointer">
          <SettingsIcon className="w-4 h-4 mr-2" />
          <span className="flex-1">{isProfileComplete ? 'Edit Profile' : 'Complete Profile'}</span>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0.5">Profile</Badge>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="cursor-pointer">
          <a
            href="https://thirteen.happyapps.dev/contributors"
            target="_blank"
            rel="noreferrer"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            <span className="flex-1">Become a Contributor</span>
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onSignOut} className="cursor-pointer text-red-600 dark:text-red-400">
          <LogOut className="w-4 h-4 mr-2" />
          <span>Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
